import {
  faBan,
  faBoltLightning,
  faCubesStacked,
  faRightLeft,
  faSackDollar,
  faShieldXmark,
  faShoppingBag,
} from '@fortawesome/pro-light-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, Icon } from 'corso-admin-ui';
import {
  CrewClaimReasonCategoryCode,
  CrewClaimResolutionMethodEnum,
  CountryCode as GeoCountryCode,
  Pegasus,
} from 'corso-types';
import { JSX, ReactNode } from 'react';
import GridList from '~/components/GridList';
import { AutomationCreate } from '~/types';
import { requiredConditionsByEditType } from './eventParams/EventOrderEditingRequiredFacts';

export type AutomationTemplate = Omit<AutomationCreate, 'rule'> & {
  rule: Omit<AutomationCreate['rule'], 'priority'>;
};

type AutomationTemplateVisual = {
  icon?: JSX.Element;
  /** Suggested to stick to prose-oriented elements. */
  instructions: ReactNode;
  automation: AutomationTemplate;
};

const automationTemplates: AutomationTemplateVisual[] = [
  {
    icon: <FontAwesomeIcon icon={faSackDollar} />,
    instructions:
      'This rule charges a handling fee when a customer opts to refund all items. The amount of the fee can be modified, and the name of the fee can be customized.',
    automation: {
      name: 'Charge Handling Fee for Refunds',
      hook: Pegasus.Hook.beforeClaimSubmission,
      isEnabled: false,
      rule: {
        conditions: {
          all: [
            {
              fact: Pegasus.Fact.claimLineItemRequestedResolutionMethod,
              operator: Pegasus.Operator.in,
              value: [CrewClaimResolutionMethodEnum.refund],
            },
            {
              fact: Pegasus.Fact.claimLineItemRequestedResolutionMethod,
              operator: Pegasus.Operator.notIn,
              value: [
                CrewClaimResolutionMethodEnum.variantExchange,
                CrewClaimResolutionMethodEnum.warrantyReview,
                CrewClaimResolutionMethodEnum.giftCard,
              ],
            },
          ],
        },
        event: {
          type: Pegasus.EventType.applyFee,
          params: {
            feeAmount: 5,
            feeDisplayName: 'Handling Fee',
            feeType: 'Fixed',
          },
        },
      },
    },
  },
  {
    icon: <FontAwesomeIcon icon={faShoppingBag} />,
    instructions:
      'This rule marks an item Final Sale, based on the product tags at the time of the order. This rule can be modified to apply to different tags, or to apply to all items.',
    automation: {
      name: 'Make Items Final Sale',
      hook: Pegasus.Hook.orderLookup,
      isEnabled: false,
      rule: {
        conditions: {
          all: [
            {
              fact: Pegasus.Fact.productTags,
              operator: Pegasus.Operator.in,
              value: ['corso-final-sale'],
            },
          ],
        },
        event: {
          type: Pegasus.EventType.modifyResolutionWindow,
          params: {
            kind: 'eligibilityDays',
            ineligibleLineItemFootnote: 'Final Sale',
            exchange: 0,
            giftCard: 0,
            refund: 0,
            warrantyReview: 0,
          },
        },
      },
    },
  },
  {
    icon: <FontAwesomeIcon icon={faShoppingBag} />,
    instructions:
      'This rule marks an item Exchange Only, based on the product tags at the time of the order. This rule can be modified to apply to different tags, or to apply to all items.',
    automation: {
      name: 'Make Items Exchange Only',
      hook: Pegasus.Hook.orderLookup,
      isEnabled: false,
      rule: {
        conditions: {
          all: [
            {
              fact: Pegasus.Fact.productTags,
              operator: Pegasus.Operator.in,
              value: ['corso-final-sale'],
            },
          ],
        },
        event: {
          type: Pegasus.EventType.modifyResolutionWindow,
          params: {
            kind: 'eligibilityDays',
            exchange: 30,
            giftCard: 0,
            refund: 0,
            warrantyReview: 0,
          },
        },
      },
    },
  },
  {
    icon: <Icon icon="fee" />,
    instructions:
      'This rule charges the customer the cost of the return label if order is not protected by Corso.',
    automation: {
      name: 'Charge Return Label Cost on Unprotected Orders',
      hook: Pegasus.Hook.beforeClaimSubmission,
      isEnabled: false,
      rule: {
        conditions: {
          all: [
            {
              fact: Pegasus.Fact.orderProtectedByCorso,
              operator: Pegasus.Operator.equal,
              value: false,
            },
          ],
        },
        event: {
          type: Pegasus.EventType.chargeForReturnLabel,
          params: {
            applyLabelMarkup: {
              shouldModify: false,
            },
          },
        },
      },
    },
  },
  {
    icon: <Icon icon="fee" />,
    instructions:
      'This rule charges the customer the cost of the return label if the return has a refund amount even if the order is protected.',
    automation: {
      name: 'Charge Return Label Cost on Protected Order Refunds',
      hook: Pegasus.Hook.beforeClaimSubmission,
      isEnabled: false,
      rule: {
        conditions: {
          all: [
            {
              fact: Pegasus.Fact.orderProtectedByCorso,
              operator: Pegasus.Operator.equal,
              value: true,
            },
            {
              fact: Pegasus.Fact.claimLineItemRequestedResolutionMethod,
              operator: Pegasus.Operator.in,
              value: [CrewClaimResolutionMethodEnum.refund],
            },
          ],
        },
        event: {
          type: Pegasus.EventType.chargeForReturnLabel,
          params: {
            applyLabelMarkup: {
              shouldModify: false,
            },
          },
        },
      },
    },
  },
  {
    icon: <Icon icon="shipment" />,
    instructions:
      'This rule deducts the shipping cost from the refund total, when a customer opts to refund all items. This rule can also be modified to apply a markup to the return shipping cost.',
    automation: {
      name: 'Deduct Shipping Cost from Refund Total',
      hook: Pegasus.Hook.beforeClaimSubmission,
      isEnabled: false,
      rule: {
        conditions: {
          all: [
            {
              fact: Pegasus.Fact.claimLineItemRequestedResolutionMethod,
              operator: Pegasus.Operator.in,
              value: [CrewClaimResolutionMethodEnum.refund],
            },
            {
              fact: Pegasus.Fact.claimLineItemRequestedResolutionMethod,
              operator: Pegasus.Operator.notIn,
              value: [
                CrewClaimResolutionMethodEnum.variantExchange,
                CrewClaimResolutionMethodEnum.warrantyReview,
                CrewClaimResolutionMethodEnum.giftCard,
              ],
            },
          ],
        },
        event: {
          type: Pegasus.EventType.chargeForReturnLabel,
          params: {
            applyLabelMarkup: {
              shouldModify: false,
            },
          },
        },
      },
    },
  },
  {
    icon: <Icon icon="shipment" />,
    instructions:
      'This rule charges for exchange order shipping if amount of the total exchange items is less than $100. This rule can also be modified to apply a markup to the exchange order shipping cost.',
    automation: {
      name: 'Charge for Exchange Order Shipping',
      hook: Pegasus.Hook.beforeClaimSubmission,
      isEnabled: false,
      rule: {
        conditions: {
          all: [
            {
              fact: Pegasus.Fact.exchangeOrderTotal,
              operator: Pegasus.Operator.lessThan,
              value: 100,
            },
          ],
        },
        event: {
          type: Pegasus.EventType.chargeForExchangeOrderShipping,
          params: {
            applyExchangeOrderShippingMarkup: {
              shouldModify: false,
            },
          },
        },
      },
    },
  },

  {
    icon: <FontAwesomeIcon icon={faSackDollar} />,
    instructions:
      'This rule waives fees on defective items. This is determined by a customer selecting a reason associated with a category of Defective Item.',
    automation: {
      name: 'Waive Fees on Defective Items',
      hook: Pegasus.Hook.beforeClaimSubmission,
      isEnabled: false,
      rule: {
        conditions: {
          all: [
            {
              fact: Pegasus.Fact.reasonCategoryCode,
              operator: Pegasus.Operator.in,
              value: [CrewClaimReasonCategoryCode.defectiveItem],
            },
          ],
        },
        event: {
          type: Pegasus.EventType.applyFee,
          params: {
            feeAmount: 0,
            feeDisplayName: '',
            feeType: 'Fixed',
          },
        },
      },
    },
  },
  {
    icon: <FontAwesomeIcon icon={faRightLeft} />,
    instructions:
      'This rule offers instant exchanges to US customers. This rule can be modified to apply to a different country, or to offer instant exchanges to all customers.',
    automation: {
      name: 'Offer Instant Exchanges to US Customers',
      hook: Pegasus.Hook.beforeClaimSubmission,
      isEnabled: false,
      rule: {
        conditions: {
          all: [
            {
              fact: Pegasus.Fact.orderCountry,
              operator: Pegasus.Operator.in,
              value: [GeoCountryCode.US],
            },
          ],
        },
        event: {
          type: Pegasus.EventType.offerInstantExchange,
          params: {
            isInstantExchangeRequired: false,
            timeToReturnBeforeCharging: 7,
          },
        },
      },
    },
  },
  {
    icon: <FontAwesomeIcon icon={faBan} />,
    instructions:
      'This rule blocks exchanges for international orders. This rule can be modified to block exchanges for a different country, this rule still allows refunds and gift cards for international orders.',
    automation: {
      name: 'Block Exchanges for International Orders',
      hook: Pegasus.Hook.orderLookup,
      isEnabled: false,
      rule: {
        conditions: {
          all: [
            {
              fact: Pegasus.Fact.orderCountry,
              operator: Pegasus.Operator.notIn,
              value: [GeoCountryCode.US],
            },
          ],
        },
        event: {
          type: Pegasus.EventType.modifyResolutionWindow,
          params: {
            kind: 'eligibilityDays',
            refund: 30,
            exchange: 0,
            giftCard: 30,
            warrantyReview: 0,
          },
        },
      },
    },
  },
  {
    icon: <Icon icon="shipment" />,
    instructions:
      'This rule charges return shipping on exchange of exchange orders. This rule can be modified to apply a markup to the return shipping cost.',
    automation: {
      name: 'Charge Shipping on Exchange of Exchange Orders',
      hook: Pegasus.Hook.beforeClaimSubmission,
      isEnabled: false,
      rule: {
        conditions: {
          all: [
            {
              fact: Pegasus.Fact.orderTags,
              operator: Pegasus.Operator.in,
              value: ['CREW-EXCHANGE-ORDER'],
            },
          ],
        },
        event: {
          type: Pegasus.EventType.chargeForReturnLabel,
          params: {
            applyLabelMarkup: {
              shouldModify: false,
            },
          },
        },
      },
    },
  },
  {
    icon: <FontAwesomeIcon icon={faBoltLightning} />,
    instructions:
      'This rule finalizes gift card and variant exchanges when the return shipment is marked as in transit. This rule will not apply to other resolution methods, such as refunds.',
    automation: {
      name: 'Finalize Gift Cards and Exchanges on Return Shipment In Transit',
      hook: Pegasus.Hook.returnShipmentWebhook,
      isEnabled: false,
      rule: {
        conditions: {
          all: [
            {
              fact: Pegasus.Fact.returnShipmentStatus,
              operator: Pegasus.Operator.in,
              value: ['in_transit'],
            },
            {
              fact: Pegasus.Fact.claimLineItemRequestedResolutionMethod,
              operator: Pegasus.Operator.in,
              value: [
                CrewClaimResolutionMethodEnum.giftCard,
                CrewClaimResolutionMethodEnum.variantExchange,
              ],
            },
            {
              fact: Pegasus.Fact.claimLineItemRequestedResolutionMethod,
              operator: Pegasus.Operator.notIn,
              value: [CrewClaimResolutionMethodEnum.refund],
            },
          ],
        },
        event: {
          type: Pegasus.EventType.autoFinalizeClaim,
          params: {},
        },
      },
    },
  },
];

const orderEditDefaultValues = {
  [Pegasus.OrderEditType.CANCEL_ORDER]: {
    editConfirmLabel:
      'If your order is eligible for cancellation, you will receive a confirmation email.',
    editSuccessLabel: 'Your order cancellation request has been sent.',
    alertMessage: null,
  },
  [Pegasus.OrderEditType.UPDATE_SHIPPING_ADDRESS]: {
    editConfirmLabel: undefined,
    editSuccessLabel: 'Your address update request has been sent.',
    alertMessage:
      'The Update Shipping Address edit does not currently support tax recalculations or shipping rate updates.',
  },
  [Pegasus.OrderEditType.REMOVE_PACKAGE_PROTECTION]: {
    editConfirmLabel:
      'Removal of shipping protection is irreversible. You will be liable for lost, damaged, or stolen items after removal. Are you sure you want to proceed?',
    editSuccessLabel:
      'Your request to remove shipping protection has been sent.',
    alertMessage: null,
  },
  [Pegasus.OrderEditType.MODIFY_QUANTITY]: {
    editConfirmLabel: 'Does this look right?',
    editSuccessLabel: 'Your order has been updated.',
    alertMessage: null,
  },
  [Pegasus.OrderEditType.UPDATE_EMAIL]: {
    editConfirmLabel: undefined,
    editSuccessLabel: 'Your order email has been updated.',
    alertMessage: null,
  },
  [Pegasus.OrderEditType.UPDATE_SHIPPING_LINE]: {
    editConfirmLabel:
      'Are you sure you would like to update your shipping method?',
    editSuccessLabel: 'Your shipping method has been updated.',
    alertMessage: null,
  },
  [Pegasus.OrderEditType.ADD_DISCOUNT_CODE]: {
    editConfirmLabel:
      'Confirm to apply this discount code. The difference will be issued as a refund.',
    editSuccessLabel: 'Your discount code has been applied.',
    alertMessage: null,
  },
} satisfies Record<
  Pegasus.OrderEditType,
  Partial<Pegasus.EventOrderEditing['params']> & {
    alertMessage: string | null;
  }
>;

// Keyed by Pegasus.OrderEditType so adding a new edit type forces a
// corresponding template here. Conditions are sourced from
// requiredConditionsByEditType to stay in sync with the rule-edit form.
// UPDATE_SHIPPING_LINE has a placeholder allowedShippingLines value until the
// create flow can seed it from the merchant's actual shipping rates.
const orderEditTemplatesByEditType = {
  [Pegasus.OrderEditType.CANCEL_ORDER]: {
    icon: <Icon icon="close" />,
    instructions:
      Pegasus.OrderEditDisplayContent[Pegasus.OrderEditType.CANCEL_ORDER]
        .merchantDescription,
    automation: {
      name: Pegasus.OrderEditDisplayContent[Pegasus.OrderEditType.CANCEL_ORDER]
        .name,
      hook: Pegasus.Hook.orderLookup,
      isEnabled: true,
      rule: {
        conditions: {
          all: [
            ...requiredConditionsByEditType[Pegasus.OrderEditType.CANCEL_ORDER],
          ],
        },
        event: {
          type: Pegasus.EventType.orderEditing,
          params: {
            editType: Pegasus.OrderEditType.CANCEL_ORDER,
            shouldNotifyOnCancellation: true,
            shouldRestockOnCancellation: true,
            ...orderEditDefaultValues[Pegasus.OrderEditType.CANCEL_ORDER],
          },
        },
      },
    },
  },
  [Pegasus.OrderEditType.UPDATE_SHIPPING_ADDRESS]: {
    icon: <Icon icon="location" />,
    instructions:
      Pegasus.OrderEditDisplayContent[
        Pegasus.OrderEditType.UPDATE_SHIPPING_ADDRESS
      ].merchantDescription,
    automation: {
      name: Pegasus.OrderEditDisplayContent[
        Pegasus.OrderEditType.UPDATE_SHIPPING_ADDRESS
      ].name,
      hook: Pegasus.Hook.orderLookup,
      isEnabled: true,
      rule: {
        conditions: {
          all: [
            ...requiredConditionsByEditType[
              Pegasus.OrderEditType.UPDATE_SHIPPING_ADDRESS
            ],
          ],
        },
        event: {
          type: Pegasus.EventType.orderEditing,
          params: { editType: Pegasus.OrderEditType.UPDATE_SHIPPING_ADDRESS },
          ...orderEditDefaultValues[
            Pegasus.OrderEditType.UPDATE_SHIPPING_ADDRESS
          ],
        },
      },
    },
  },
  [Pegasus.OrderEditType.REMOVE_PACKAGE_PROTECTION]: {
    icon: <FontAwesomeIcon icon={faShieldXmark} />,
    instructions:
      Pegasus.OrderEditDisplayContent[
        Pegasus.OrderEditType.REMOVE_PACKAGE_PROTECTION
      ].merchantDescription,
    automation: {
      name: Pegasus.OrderEditDisplayContent[
        Pegasus.OrderEditType.REMOVE_PACKAGE_PROTECTION
      ].name,
      hook: Pegasus.Hook.orderLookup,
      isEnabled: true,
      rule: {
        conditions: {
          all: [
            ...requiredConditionsByEditType[
              Pegasus.OrderEditType.REMOVE_PACKAGE_PROTECTION
            ],
          ],
        },
        event: {
          type: Pegasus.EventType.orderEditing,
          params: {
            editType: Pegasus.OrderEditType.REMOVE_PACKAGE_PROTECTION,
            ...orderEditDefaultValues[
              Pegasus.OrderEditType.REMOVE_PACKAGE_PROTECTION
            ],
          },
        },
      },
    },
  },
  [Pegasus.OrderEditType.MODIFY_QUANTITY]: {
    icon: <FontAwesomeIcon icon={faCubesStacked} />,
    instructions:
      Pegasus.OrderEditDisplayContent[Pegasus.OrderEditType.MODIFY_QUANTITY]
        .merchantDescription,
    automation: {
      name: Pegasus.OrderEditDisplayContent[
        Pegasus.OrderEditType.MODIFY_QUANTITY
      ].name,
      hook: Pegasus.Hook.orderLookup,
      isEnabled: true,
      rule: {
        conditions: {
          all: [
            ...requiredConditionsByEditType[
              Pegasus.OrderEditType.MODIFY_QUANTITY
            ],
          ],
        },
        event: {
          type: Pegasus.EventType.orderEditing,
          params: {
            editType: Pegasus.OrderEditType.MODIFY_QUANTITY,
            quantityModificationType: 'BOTH',
            quantityModificationValue: 1,
            ...orderEditDefaultValues[Pegasus.OrderEditType.MODIFY_QUANTITY],
          },
        },
      },
    },
  },
  [Pegasus.OrderEditType.UPDATE_EMAIL]: {
    icon: <Icon icon="email" />,
    instructions:
      Pegasus.OrderEditDisplayContent[Pegasus.OrderEditType.UPDATE_EMAIL]
        .merchantDescription,
    automation: {
      name: Pegasus.OrderEditDisplayContent[Pegasus.OrderEditType.UPDATE_EMAIL]
        .name,
      hook: Pegasus.Hook.orderLookup,
      isEnabled: true,
      rule: {
        conditions: {
          all: [
            ...requiredConditionsByEditType[Pegasus.OrderEditType.UPDATE_EMAIL],
          ],
        },
        event: {
          type: Pegasus.EventType.orderEditing,
          params: {
            editType: Pegasus.OrderEditType.UPDATE_EMAIL,
            ...orderEditDefaultValues[Pegasus.OrderEditType.UPDATE_EMAIL],
          },
        },
      },
    },
  },
  [Pegasus.OrderEditType.UPDATE_SHIPPING_LINE]: {
    icon: <Icon icon="shipment" />,
    instructions:
      Pegasus.OrderEditDisplayContent[
        Pegasus.OrderEditType.UPDATE_SHIPPING_LINE
      ].merchantDescription,
    automation: {
      name: Pegasus.OrderEditDisplayContent[
        Pegasus.OrderEditType.UPDATE_SHIPPING_LINE
      ].name,
      hook: Pegasus.Hook.orderLookup,
      isEnabled: true,
      rule: {
        conditions: {
          all: [
            ...requiredConditionsByEditType[
              Pegasus.OrderEditType.UPDATE_SHIPPING_LINE
            ],
          ],
        },
        event: {
          type: Pegasus.EventType.orderEditing,
          params: {
            editType: Pegasus.OrderEditType.UPDATE_SHIPPING_LINE,
            allowedShippingLines: [],
            ...orderEditDefaultValues[
              Pegasus.OrderEditType.UPDATE_SHIPPING_LINE
            ],
          },
        },
      },
    },
  },
  [Pegasus.OrderEditType.ADD_DISCOUNT_CODE]: {
    icon: <Icon icon="dollar" />,
    instructions:
      Pegasus.OrderEditDisplayContent[Pegasus.OrderEditType.ADD_DISCOUNT_CODE]
        .merchantDescription,
    automation: {
      name: Pegasus.OrderEditDisplayContent[
        Pegasus.OrderEditType.ADD_DISCOUNT_CODE
      ].name,
      hook: Pegasus.Hook.orderLookup,
      isEnabled: true,
      rule: {
        conditions: {
          all: [
            ...requiredConditionsByEditType[
              Pegasus.OrderEditType.ADD_DISCOUNT_CODE
            ],
          ],
        },
        event: {
          type: Pegasus.EventType.orderEditing,
          params: {
            editType: Pegasus.OrderEditType.ADD_DISCOUNT_CODE,
            ...orderEditDefaultValues[Pegasus.OrderEditType.ADD_DISCOUNT_CODE],
          },
        },
      },
    },
  },
} satisfies Record<Pegasus.OrderEditType, AutomationTemplateVisual>;

/** Store Rule templates with a runtime generated random `id` for selection/data passing of a unique key. */
export const automationTemplatesWithIds = [
  ...automationTemplates,
  ...Object.values(orderEditTemplatesByEditType),
].map((template) => ({
  id: `${Math.random().toString(36).slice(2)}`, // not using crypto.randomUUID() for compatibility with older browsers on app load, so they can see the outdated banner
  ...template,
}));

export default function AutomationTemplates() {
  return (
    <Card flush>
      <GridList data={automationTemplatesWithIds}>
        {({ id, icon, automation: { name } }) => (
          <GridList.ItemLink
            key={id}
            label={name}
            to={{
              pathname: 'create',
              search: `?template=${id}`,
            }}
            icon={icon ?? <Icon icon="aiInline" aria-hidden="true" />}
            secondaryIcon={<Icon icon="add" aria-hidden="true" />}
            variant="primary"
          />
        )}
      </GridList>
    </Card>
  );
}
